// RPTLAYOUT1-F1-S1 (docs/build-briefs/RPTLAYOUT1-F1-S1-reports-home-results.md; design/mockups/reports-experience.html, approved
// 2026-10-19): the Reports HOME and RESULTS screens, one component for the location console and the franchisor console. The hosts
// keep the session, the scope and every request; the server keeps every figure. This file draws what the server returned.
//
// Home: Favorites and Recently run side by side, then plain headed cards, a star on each. Results: one control row (Dates,
// Locations for the franchisor only, Schedule email, Export CSV), the chart, the totals, and a sortable table with a pinned
// total row. One report runs at a time (DEC-435): every run control is disabled while one is in flight.
import {
  CANNED_FAMILIES,
  cannedLinkOnly,
  cannedRunBody,
  hasNoDates,
  isCannedCatalog,
  isCannedResult,
  record,
  reportWords,
  type CannedCatalogDto,
  type CannedCatalogEntry,
  type CannedFilters,
  type CannedResultDto,
} from "./cannedReportModel.ts";
import { cannedReplyMessage, scheduleHost, type CannedFrame, type CannedReportHost } from "./cannedReport.ts";
import { saveBlob } from "./consoleDownload.ts";
import { formatCents } from "./money.ts";
import { mountLocationPicker } from "./locationPicker.ts";
import { toFilterScope, pickerValue, type PickerLocation, type PickerMode } from "./locationPickerModel.ts";
import { renderChartCard } from "./reportChart.ts";
import type { ChartResult } from "./reportChartModel.ts";
import { mountReportScheduleDialog } from "./reportSchedules.ts";
import {
  NO_MARKS,
  catalogCards,
  chartModel,
  compareKind,
  datesSummary,
  favoriteCards,
  groupedCards,
  isFavorite,
  isEmptyResult,
  isMarks,
  nextSort,
  owedPerPerson,
  payPeriodChoices,
  type PayPeriodChoice,
  savedCards,
  savedGroups,
  savedItems,
  templateItems,
  presetChoices,
  recentRows,
  resultTable,
  runFailure,
  CUSTOMER_WORDS,
  LOCATION_WORDS,
  runLabel,
  scopeLabel,
  sortRows,
  startingFilters,
  defaultSort,
  totalFigures,
  whenText,
  withCompare,
  withFavorite,
  withRun,
  type ExternalReportCard,
  type ReportCard,
  type ReportMarks,
  type TableRow,
  type RunFailure,
  type SavedRef,
  type ScopeWords,
  type TableSort,
  displayTitle,
} from "./reportsExperienceModel.ts";
import { bindHoverTip, hideHoverHelp, hoverHelp } from "./hoverHelp.ts";
import { createExportButtons } from "./reportExportButtons.ts";
import { createReportsDashboard, type ReportsDashboard, type TilesApi } from "./reportsDashboard.ts";
import type { DashboardSource, OpenTarget } from "./reportsDashboardModel.ts";
import { tileFigure } from "./reportTiles.ts";
import { displayTable, rowDetails, type DisplayTable } from "./reportTableModel.ts";
import { exportMenu, filterPill, pillWrap, type ToolbarItem } from "./reportToolbar.ts";

export interface ReportsExperienceHost extends CannedReportHost {
  /** The person's marks route: GET the list, POST /favorite and POST /run. */
  marksPrefix: string;
  /** New custom report: the host opens its existing builder. Absent when this person may not build, and then no button is drawn. */
  newCustomReport?(): void;
  /** Reports outside the canned catalog that the host still opens itself (the location console's own report views). */
  externalCards?: readonly ExternalReportCard[];
  openExternal?(id: string): void;
  /**
   * RPTMINE1: saved custom reports (and, in the location console, templates) on the home. Present only when this person may open
   * them, so the lists are never requested otherwise. `open` shows one in the host's existing screen, which runs it on open.
   */
  saved?: { templates: boolean; open(ref: SavedRef): void };
  /** A canned report id to open as soon as the catalog loads (an old address for a retired report). Ignored when it is not runnable. */
  openOnLoad?: string;
  /** Called once the home has acted on `openOnLoad` (opened the report, or found it not runnable), so the host forgets the request. */
  usedOnLoad?(): void;
  /** The clock, for "Today 9:05 AM". Tests pass their own. */
  now?: () => Date;
  /**
   * REPORTDASH1: the dashboard above the library. `source` says which figures, previews and attention rows this console has and
   * words them; `openAction` opens a screen the host owns (an attention row's fix). A target the host cannot open is not offered.
   */
  dashboard?: { source: DashboardSource; openAction?(id: string): void; canAction?(id: string): boolean };
  /** RPTLAYOUT1-F1-S4: what the scope picks from. The Software Owner console reads across customers; every other console, locations. */
  scopeNoun?: "customers" | "locations";
  /**
   * CLOSEFIX1 (manager-day-01): the location's open pay periods from its payroll connection, for the Tips by employee dates.
   * Absent when this person may not read them or no payroll is connected, and then no pay period choice is drawn.
   */
  payPeriods?(): Promise<ReadonlyArray<{ periodStart: string; periodEnd: string }>>;
}

type RunStatus = "idle" | "loading" | "ok" | "empty" | RunFailure;

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}
/** HIGFIX4B (F-44): a row with a figure column and every figure zero or absent: nothing happened on that line. */
function isIdleRow(columns: readonly { key: string; numeric: boolean; change: boolean }[], row: TableRow): boolean {
  const figures = columns.filter((column) => column.numeric && !column.change);
  return figures.length > 0 && figures.every((column) => {
    const value = row.values[column.key];
    return value === null || value === undefined || value === 0;
  });
}
/** HIGFIX4B (F-43): how many of a chart's points carry a figure other than zero. */
function chartPointsWithData(model: { categories: readonly unknown[]; series: readonly { values: readonly (number | null)[] }[] }): number {
  return model.categories.filter((_, index) => model.series.some((entry) => {
    const value = entry.values[index];
    return value !== null && value !== undefined && value !== 0;
  })).length;
}
function button(text: string, className: string, action: () => void, testid?: string): HTMLButtonElement {
  const element = node("button", className, text);
  element.type = "button";
  element.addEventListener("click", action);
  if (testid !== undefined) element.dataset["testid"] = testid;
  return element;
}
function starButton(title: string, on: boolean, toggle: () => void, testid: string): HTMLButtonElement {
  const star = button(on ? "★" : "☆", `rx-star${on ? " on" : ""}`, toggle, testid);
  star.setAttribute("aria-pressed", String(on));
  star.setAttribute("aria-label", on ? `Remove ${title} from favorites` : `Keep ${title} as a favorite`);
  return star;
}

export function mountReportsExperience(target: HTMLElement, host: ReportsExperienceHost): CannedFrame {
  const now = host.now ?? (() => new Date());
  const words: ScopeWords = host.scopeNoun === "customers" ? CUSTOMER_WORDS : LOCATION_WORDS;
  let alive = true;
  let generation = 0;
  let catalog: CannedCatalogDto | undefined;
  let catalogError = "";
  let savedNote = "";
  let savedLoaded: ReportCard[] = [];
  let savedToken = 0;
  let marks: ReportMarks = NO_MARKS;
  let marksNotice = "";
  let query = "";
  let view: "home" | "results" = "home";
  let entry: CannedCatalogEntry | undefined;
  let applied: CannedFilters | undefined;
  const lastFilters = new Map<string, CannedFilters>();
  let status: RunStatus = "idle";
  let failure = "";
  let result: CannedResultDto | undefined;
  let ranBody: Record<string, unknown> | undefined;
  let sort: TableSort | null = null;
  let popover: "dates" | "locations" | "export" | null = null;
  /** UIFIX1 (SF-2): the chart is collapsible. It opens on a screen wide enough for it and stays as the person left it. */
  let chartOpen: boolean | undefined;
  /** UIFIX1 (SF-4): the row whose full detail is open in the row sheet (anything cut off in the table is reachable by tap). */
  let detail: { row: TableRow; view: DisplayTable } | undefined;
  let customDraft = { from: "", to: "" };
  let payPeriodList: ReadonlyArray<{ periodStart: string; periodEnd: string }> = [];
  if (host.payPeriods !== undefined) {
    void host.payPeriods().then(
      (periods) => {
        payPeriodList = periods;
        if (alive) draw();
      },
      () => undefined,
    );
  }
  let customError = "";
  let sessions: Array<{ id: string; label: string }> = [];
  let sessionsNote = "";
  let saved: { id: string; version: number } | undefined;
  let requestId: string | undefined;
  let notice = "";
  let noticeLink = "";
  /** The notice text that reports something that worked (drawn as plain text, not as an error). */
  let okNotice = "";
  /** REPORTDASH1: the All reports drawer, and the dashboard (created once the catalog says what this person can open). */
  let drawerOpen = false;
  let dash: ReportsDashboard | undefined;

  const root = node("section", "rx");
  root.dataset["testid"] = "rx";
  const screen = node("div", "rx-screen");
  root.append(screen);
  target.append(root);
  const active = () => alive && host.active() && root.isConnected;
  const busy = () => status === "loading";
  // RPTEXPORT1: Export PDF and Export to Google Sheets beside Export CSV, for the report on screen.
  const exportButtons = createExportButtons(host, {
    testid: "rx",
    target: () =>
      entry !== undefined && ranBody !== undefined && entry.canExport && result !== undefined
        ? { path: `${host.prefix}/${CANNED_FAMILIES[entry.id]!.route}/export`, body: ranBody }
        : undefined,
    disabled: () => busy(),
    notify: (message, link, ok) => {
      notice = message;
      noticeLink = link ?? "";
      okNotice = ok === true ? message : "";
      if (active()) draw();
    },
    redraw: () => {
      if (active()) draw();
    },
  });
  const cards = (): ReportCard[] => [...catalogCards(catalog?.reports ?? [], host.externalCards ?? []), ...savedLoaded];

  // ---------------------------------------------------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------------------------------------------------
  async function loadCatalog(): Promise<void> {
    const captured = ++generation;
    catalogError = "";
    draw();
    try {
      const reply = await host.request<unknown>(
        "GET",
        `${host.prefix}/catalog${host.locationId ? `?locationId=${encodeURIComponent(host.locationId)}` : ""}`,
      );
      if (!active() || captured !== generation) return;
      if (reply.status !== 200 || !isCannedCatalog(reply.body)) catalogError = cannedReplyMessage(reply);
      else catalog = reply.body;
    } catch {
      if (active() && captured === generation) catalogError = "Could not load reports. Try again.";
    } finally {
      if (active() && captured === generation) {
        if (catalog !== undefined) startDashboard();
        draw();
        if (catalog !== undefined) void loadSaved();
        const wanted = host.openOnLoad;
        const first = wanted === undefined || catalog === undefined ? undefined : catalogCards(catalog.reports, []).find((item) => item.id === wanted);
        if (wanted !== undefined) host.usedOnLoad?.();
        if (first !== undefined && first.enabled) open(first);
      }
    }
  }
  // ---------------------------------------------------------------------------------------------------------------------
  // REPORTDASH1: the dashboard above the library
  // ---------------------------------------------------------------------------------------------------------------------
  /** A target is offered only when this person can open it: the catalog says the canned report can run, or the console offers the card or screen. */
  function dashboardCanOpen(tile: OpenTarget, range?: { from: string; to: string }): boolean {
    if (tile.kind === "action") return host.dashboard?.openAction !== undefined && (host.dashboard.canAction === undefined || host.dashboard.canAction(tile.id));
    if (tile.kind === "external") return host.externalCards?.some((card) => card.id === tile.id) === true;
    const found = catalog?.reports.find((report) => report.id === tile.id);
    if (found === undefined || !found.canRun || found.template === null) return false;
    const dates = tile.range ?? range;
    if (tile.preset === "custom" || tile.range !== undefined) return dates !== undefined;
    return tile.preset === undefined || found.datePresets?.includes(tile.preset) === true;
  }
  function dashboardOpen(tile: OpenTarget, range?: { from: string; to: string }): void {
    if (tile.kind === "action") {
      host.dashboard?.openAction?.(tile.id);
      return;
    }
    const card = cards().find((entryCard) => entryCard.id === tile.id);
    if (card === undefined) return;
    if (tile.kind === "external") {
      open(card);
      return;
    }
    const dates = tile.range ?? range;
    open(card, dates !== undefined ? { preset: "custom", from: dates.from, to: dates.to } : tile.preset !== undefined && tile.preset !== "custom" ? { preset: tile.preset } : {});
  }
  /** CRB10: the person's pinned one-number reports, through the existing personal tile routes. */
  function tilesApi(): TilesApi | undefined {
    const gate = host.saved;
    if (host.dashboard?.source.tiles !== true || gate === undefined) return undefined;
    const base = `${host.customPrefix}/tiles`;
    return {
      canManage: host.newCustomReport !== undefined,
      async list() {
        const reply = await host.request<unknown>("GET", base);
        if (reply.status !== 200 || !record(reply.body) || !Array.isArray(reply.body["items"])) return undefined;
        return reply.body["items"]
          .filter(record)
          .filter((item) => typeof item["id"] === "string")
          .slice(0, 8)
          .map((item) => ({
            id: item["id"] as string,
            available: item["available"] === true,
            ...(typeof item["definitionId"] === "string" ? { definitionId: item["definitionId"] } : {}),
            ...(typeof item["label"] === "string" ? { label: item["label"] } : {}),
          }));
      },
      async run(id) {
        const reply = await host.request<unknown>("POST", `${base}/${encodeURIComponent(id)}/run`);
        if (reply.status === 403 || reply.status === 404) return "unavailable";
        if (reply.status !== 200 || !record(reply.body)) return undefined;
        return tileFigure(reply.body as unknown as Parameters<typeof tileFigure>[0]);
      },
      async order(ids) {
        return (await host.request<unknown>("POST", `${base}/order`, { ids })).status === 200;
      },
      async remove(id) {
        return (await host.request<unknown>("POST", `${base}/${encodeURIComponent(id)}/remove`)).status === 200;
      },
      open(definitionId) {
        gate.open({ kind: "saved", id: definitionId, section: "mine" });
      },
    };
  }
  function startDashboard(): void {
    const config = host.dashboard;
    if (config === undefined || dash !== undefined) return;
    const tiles = tilesApi();
    dash = createReportsDashboard({
      source: config.source,
      canOpen: dashboardCanOpen,
      open: dashboardOpen,
      ...(tiles === undefined ? {} : { tiles }),
      active: () => alive && host.active(),
    });
    void dash.load();
  }

  /** RPTMINE1: the saved lists load after the catalog and never hold the canned cards back. A failed list is one line, not an error page. */
  async function loadSaved(): Promise<void> {
    const gate = host.saved;
    if (gate === undefined) return;
    const captured = ++savedToken;
    savedNote = "";
    try {
      const [savedReply, templateReply] = await Promise.all([
        host.request<unknown>("GET", `${host.customPrefix}/saved?tab=all`),
        gate.templates ? host.request<unknown>("GET", `${host.customPrefix}/templates`) : Promise.resolve(undefined),
      ]);
      if (!active() || captured !== savedToken) return;
      const savedRows = savedReply.status === 200 ? savedItems(savedReply.body) : undefined;
      const templates = templateReply === undefined ? [] : templateReply.status === 200 ? templateItems(templateReply.body) : undefined;
      // A refusal (a person who cannot open saved reports) is no card and no announcement; any other failure is one line.
      const refused = savedReply.status === 403 || templateReply?.status === 403;
      savedNote = (savedRows === undefined || templates === undefined) && !refused ? "Saved reports did not load." : "";
      savedLoaded = savedCards(savedRows, templates);
    } catch {
      if (active() && captured === savedToken) savedNote = "Saved reports did not load.";
    }
    if (active() && view === "home") draw();
  }
  async function loadMarks(): Promise<void> {
    try {
      const reply = await host.request<unknown>("GET", host.marksPrefix);
      if (!active()) return;
      if (reply.status === 200 && isMarks(reply.body)) {
        marks = reply.body;
        if (view === "home") draw();
      }
    } catch {
      // Marks are a convenience. The catalog still lists every report.
    }
  }
  async function setFavorite(id: string, on: boolean): Promise<void> {
    const before = marks;
    marks = withFavorite(marks, id, on);
    marksNotice = "";
    draw();
    try {
      const reply = await host.request<unknown>("POST", `${host.marksPrefix}/favorite`, { reportId: id, favorite: on });
      if (!active()) return;
      if (reply.status === 200 && isMarks(reply.body)) marks = reply.body;
      else {
        marks = before;
        marksNotice = record(reply.body) && typeof reply.body["message"] === "string" ? reply.body["message"] : "Could not save the favorite. Try again.";
      }
    } catch {
      if (active()) {
        marks = before;
        marksNotice = "Could not save the favorite. Try again.";
      }
    }
    if (active()) draw();
  }
  async function recordRun(id: string, label: string): Promise<void> {
    marks = withRun(marks, id, label, now().toISOString());
    try {
      const reply = await host.request<unknown>("POST", `${host.marksPrefix}/run`, { reportId: id, label });
      if (active() && reply.status === 200 && isMarks(reply.body)) marks = reply.body;
    } catch {
      // The run already shows. Only the Recently run list misses it.
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Running
  // ---------------------------------------------------------------------------------------------------------------------
  function open(card: ReportCard, override?: Partial<CannedFilters>): void {
    if (!card.enabled || busy()) return;
    drawerOpen = false;
    if (card.saved !== undefined) {
      // A saved report or template runs in the host's own screen. It still counts as run for Recently run.
      void recordRun(card.id, card.saved.kind === "template" ? "Template" : card.saved.section === "shared" ? "Shared report" : "My report");
      host.saved?.open(card.saved);
      return;
    }
    if (card.external) {
      host.openExternal?.(card.id);
      return;
    }
    const next = catalog?.reports.find((candidate) => candidate.id === card.id);
    if (next === undefined || catalog === undefined) return;
    generation++;
    entry = next;
    // REPORTDASH1: a dashboard tile opens its report with the dates the tile shows, not the dates last used.
    applied = override !== undefined ? { ...startingFilters(next, catalog), ...override } : (lastFilters.get(next.id) ?? startingFilters(next, catalog));
    view = "results";
    popover = null;
    result = undefined;
    ranBody = undefined;
    saved = undefined;
    requestId = undefined;
    sort = null;
    sessions = [];
    sessionsNote = "";
    notice = "";
    noticeLink = "";
    void run();
  }
  function goHome(): void {
    generation++;
    view = "home";
    popover = null;
    status = "idle";
    result = undefined;
    ranBody = undefined;
    draw();
    // A report saved from the results (Schedule email saves it) is on the home as soon as the person is back on it.
    void loadSaved();
    void dash?.load();
  }
  async function run(): Promise<void> {
    if (!catalog || !entry || !applied) return;
    let body: Record<string, unknown>;
    // UIFIX5: a count report with no closed count chosen yet opens on its picker with the choices loaded, not on an error.
    const needsCounts = entry.requiresCountSession === true || entry.requiresTwoCountSessions === true;
    const countsChosen =
      entry.requiresTwoCountSessions === true
        ? applied.baselineCountSessionId !== "" && applied.comparisonCountSessionId !== ""
        : applied.countSessionId !== "";
    if (needsCounts && !countsChosen) {
      status = "idle";
      failure = "";
      result = undefined;
      draw();
      if (sessions.length === 0 && sessionsNote === "") void loadSessions();
      return;
    }
    try {
      if (!entry.canRun || entry.template === null || !CANNED_FAMILIES[entry.id]) throw new Error("This report is not available.");
      body = cannedRunBody(entry, applied, catalog);
    } catch (cause) {
      status = "error";
      failure = cause instanceof Error ? cause.message : "Choose report options.";
      result = undefined;
      draw();
      return;
    }
    const captured = ++generation;
    const reportEntry = entry;
    const filters = structuredClone(applied);
    lastFilters.set(reportEntry.id, filters);
    status = "loading";
    failure = "";
    result = undefined;
    ranBody = undefined;
    saved = undefined;
    requestId = undefined;
    sort = null;
    notice = "";
    draw();
    try {
      const reply = await host.request<unknown>("POST", `${host.prefix}/${CANNED_FAMILIES[reportEntry.id]!.route}/run`, body);
      if (!active() || captured !== generation) return;
      if (
        reply.status !== 200 ||
        !isCannedResult(reply.body) ||
        reply.body.template.id !== CANNED_FAMILIES[reportEntry.id]!.origin ||
        (record(body["categories"]) && body["categories"]["kind"] === "named"
          ? reply.body.template.hash !== body["templateHash"] || reply.body.template.version !== body["templateVersion"]
          : reply.body.template.hash !== reportEntry.template?.hash)
      ) {
        status = reply.status === 200 ? "error" : runFailure(reply.status);
        failure = cannedReplyMessage(reply);
        return;
      }
      result = reply.body;
      ranBody = body;
      status = isEmptyResult(reply.body) ? "empty" : "ok";
      void recordRun(reportEntry.id, runLabel(filters, catalog, words));
    } catch {
      if (active() && captured === generation) {
        status = "error";
        failure = "Could not load this report. Try again.";
      }
    } finally {
      if (active() && captured === generation) draw();
    }
  }
  function change(next: CannedFilters, rerun = true): void {
    if (busy()) return;
    applied = next;
    if (rerun) void run();
    else draw();
  }
  async function exportCsv(): Promise<void> {
    if (busy() || !entry || !ranBody || !entry.canExport) return;
    const captured = generation;
    const route = CANNED_FAMILIES[entry.id]!.route;
    notice = "";
    try {
      const reply = await host.file(`${host.prefix}/${route}/export?format=csv`, ranBody);
      if (!active() || captured !== generation) return;
      if (reply.status === 200 && reply.blob) saveBlob(reply.blob, reply.filename ?? "report.csv");
      else notice = reply.message ?? "Could not download this report.";
    } catch {
      notice = "Could not download this report.";
    }
    if (active() && captured === generation) draw();
  }
  async function schedule(): Promise<void> {
    if (busy() || !entry || !ranBody || !result || !entry.canSchedule) return;
    const captured = generation;
    const route = CANNED_FAMILIES[entry.id]!.route;
    const reportEntry = entry;
    notice = "";
    try {
      if (saved === undefined) {
        requestId ??= crypto.randomUUID();
        const reply = await host.request<unknown>("POST", `${host.prefix}/${route}/save`, {
          ...ranBody,
          requestId,
          title: reportEntry.title,
        });
        if (!active() || captured !== generation) return;
        if (reply.status !== 200 || !record(reply.body) || typeof reply.body["id"] !== "string" || !Number.isSafeInteger(reply.body["currentVersion"])) {
          notice = cannedReplyMessage(reply);
          draw();
          return;
        }
        saved = { id: reply.body["id"], version: Number(reply.body["currentVersion"]) };
      }
      mountReportScheduleDialog(
        root,
        scheduleHost(host, () => active() && captured === generation, saved.id),
        { definitionId: saved.id, version: saved.version, ...(cannedLinkOnly(result.template) ? { linkOnly: true } : {}) },
      );
    } catch {
      notice = "Could not save this report. Try again.";
      if (active()) draw();
    }
  }
  async function loadSessions(): Promise<void> {
    if (busy() || !catalog || !applied) return;
    const summary = catalog.reports.find((candidate) => candidate.id === "closed-count-session-summary" && candidate.canRun);
    if (!summary) {
      sessionsNote = "Closed counts are not available.";
      draw();
      return;
    }
    try {
      const reply = await host.request<unknown>(
        "POST",
        `${host.prefix}/closed-count-session-summary/run`,
        cannedRunBody(summary, { ...applied, chartBasis: "date" }, catalog),
      );
      if (!active()) return;
      if (reply.status !== 200 || !isCannedResult(reply.body)) sessionsNote = cannedReplyMessage(reply);
      else if (reply.body.display.truncated) sessionsNote = "Choose a shorter range to pick from every closed count.";
      else {
        sessions = reply.body.rows
          .filter((row) => typeof row["sessionId"] === "string")
          .map((row) => ({ id: String(row["sessionId"]), label: `${String(row["businessDate"])} ${String(row["locationName"])}` }));
        sessionsNote = sessions.length === 0 ? "No closed counts in these dates." : "";
      }
    } catch (cause) {
      sessionsNote = cause instanceof Error ? cause.message : "Could not load closed counts.";
    }
    if (active()) draw();
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Drawing
  // ---------------------------------------------------------------------------------------------------------------------
  let escapeHandler: ((event: KeyboardEvent) => void) | undefined;
  function stopEscape(): void {
    if (escapeHandler !== undefined) document.removeEventListener("keydown", escapeHandler);
    escapeHandler = undefined;
  }
  function closePopover(): void {
    popover = null;
    draw();
  }
  function draw(): void {
    if (!active()) return;
    hideHoverHelp();
    stopEscape();
    screen.replaceChildren();
    if (catalog === undefined) {
      screen.append(startState());
      return;
    }
    if (view === "results" && entry !== undefined && applied !== undefined) screen.append(resultsView(entry, applied, catalog));
    else {
      screen.append(homeView());
      if (drawerOpen && dash !== undefined) screen.append(drawerView());
    }
    const sheetEl = view === "results" ? rowSheet() : undefined;
    if (sheetEl !== undefined) screen.append(sheetEl);
    if (popover !== null || detail !== undefined || (drawerOpen && view === "home")) {
      escapeHandler = (event) => {
        if (event.key !== "Escape") return;
        if (detail !== undefined) {
          detail = undefined;
          draw();
        } else if (popover !== null) closePopover();
        else if (drawerOpen) closeDrawer();
      };
      document.addEventListener("keydown", escapeHandler);
    }
  }
  function startState(): HTMLElement {
    const state = node("div", "rx-card rx-state");
    state.dataset["testid"] = catalogError === "" ? "rx-state-loading" : "rx-state-error";
    if (catalogError === "") {
      state.setAttribute("role", "status");
      state.setAttribute("aria-label", "Loading reports");
      for (const width of ["40%", "100%", "100%", "70%"]) {
        const line = node("div", "rx-skel");
        line.style.width = width;
        state.append(line);
      }
      return state;
    }
    state.setAttribute("role", "alert");
    state.append(node("h2", undefined, "Reports did not load"), node("p", undefined, catalogError), button("Try again", "rx-btn rx-btn-primary", () => void loadCatalog(), "rx-retry"));
    return state;
  }

  // ----- home
  function card(item: ReportCard): HTMLElement {
    const element = node("div", `rx-card-report${item.enabled ? "" : " disabled"}`);
    element.dataset["testid"] = `rx-card-${item.id}`;
    const full = item.fullTitle ?? item.title;
    const openButton = button(item.title, "rx-card-open", () => open(item), `rx-open-${item.id}`);
    openButton.disabled = !item.enabled || busy();
    if (item.meta !== undefined && item.meta !== "") {
      openButton.textContent = "";
      openButton.append(node("span", "rx-card-title", item.title), node("small", "rx-card-meta", item.meta));
    } else {
      openButton.textContent = "";
      openButton.append(node("span", "rx-card-title", item.title));
    }
    // RPTHOME-F1: the star is the only card control. The full title is the accessible name. RPTHOME1-F2: the short
    // description (and, on a disabled card, its unavailable reason) is the aria-description and the styled tooltip text;
    // there is no native title attribute.
    if (item.noPermission) openButton.setAttribute("aria-label", `${full}, no access`);
    else if (item.fullTitle !== undefined) openButton.setAttribute("aria-label", full);
    const tip = [item.description, item.reason].filter((part): part is string => part !== undefined && part !== "").join(" ");
    // HELPHOVER1: one shared hover tooltip (hoverHelp.ts); the open button carries the aria-description.
    element.append(openButton);
    bindHoverTip(element, tip, openButton);
    // Nothing is recorded to build this report from: the card stays, disabled, with the reason in its tooltip and description.
    if (item.reason !== undefined) element.dataset["unavailable"] = "true";
    if (item.enabled) element.append(starButton(item.title, isFavorite(marks, item.id), () => void setFavorite(item.id, !isFavorite(marks, item.id)), `rx-star-${item.id}`));
    return element;
  }
  /** Favorites and Recently run, then the grouped library: the same cards, stars, saved groups and search the home always had. */
  function libraryParts(search: HTMLInputElement): HTMLElement[] {
    const all = cards();
    // REPORTUX1 (baseline 10, Kerry 2026-10-23): Favorites and Recently run are one compact strip above the library, two short
    // rows, so the library starts near the top. Nothing moves to another screen; an empty row is one line, not a card.
    const strip = node("div", "rx-card rx-strip");
    strip.dataset["testid"] = "rx-strip";
    const stripRow = (testid: string, label: string): { row: HTMLElement; items: HTMLElement } => {
      const row = node("div", "rx-strip-row");
      row.dataset["testid"] = testid;
      const items = node("div", "rx-strip-items");
      row.append(node("h2", "rx-section rx-strip-label", label), items);
      strip.append(row);
      return { row, items };
    };
    const favorites = favoriteCards(all, marks);
    const favoritesRow = stripRow("rx-favorites", "Favorites");
    for (const item of favorites) favoritesRow.items.append(card(item));
    if (favorites.length === 0) favoritesRow.items.append(node("p", "rx-strip-empty", "No favorites yet. Star a report below to keep it here."));
    const recents = recentRows(all, marks, now());
    const recentRow = stripRow("rx-recent", "Recently run");
    for (const row of recents) {
      const item = node("button", "rx-recent-row");
      item.type = "button";
      item.dataset["testid"] = `rx-recent-${row.card.id}`;
      const text = node("span", "rx-recent-text");
      text.append(node("b", undefined, row.card.title), node("small", undefined, row.label));
      item.append(text, node("span", "rx-recent-when", row.when));
      item.disabled = busy();
      item.addEventListener("click", () => open(row.card));
      recentRow.items.append(item);
    }
    if (recents.length === 0) recentRow.items.append(node("p", "rx-strip-empty", "Nothing run yet"));
    const groupsEl = node("div", "rx-groups");
    groupsEl.dataset["testid"] = "rx-groups";
    const drawGroups = () => {
      groupsEl.replaceChildren();
      // RPTMINE1: the person's own saved reports come first, then what was shared with them, then templates, then the catalog.
      const mine = savedGroups(all, query);
      for (const group of mine) {
        const section = node("section", "rx-group");
        section.dataset["testid"] = `rx-saved-${group.id}`;
        section.append(node("h2", "rx-section", group.label));
        const grid = node("div", "rx-grid");
        for (const item of group.cards) grid.append(card(item));
        section.append(grid);
        groupsEl.append(section);
      }
      if (savedNote !== "" && host.saved !== undefined) {
        const note = node("p", "rx-notice", savedNote);
        note.setAttribute("role", "status");
        note.dataset["testid"] = "rx-saved-note";
        note.append(" ", button("Try again", "rx-btn", () => void loadSaved(), "rx-saved-retry"));
        groupsEl.append(note);
      }
      const groups = groupedCards(all, query);
      for (const group of groups) {
        const section = node("section", "rx-group");
        section.dataset["testid"] = `rx-group-${group.id}`;
        section.append(node("h2", "rx-section", group.label));
        const grid = node("div", "rx-grid");
        for (const item of group.cards) grid.append(card(item));
        section.append(grid);
        groupsEl.append(section);
      }
      if (groups.length === 0 && mine.length === 0) {
        const none = node("p", "rx-empty-line", query.trim() === "" ? "No reports are available to you." : "No report matches your search.");
        none.dataset["testid"] = "rx-no-match";
        groupsEl.append(none);
      }
    };
    search.addEventListener("input", () => {
      query = search.value;
      drawGroups();
    });
    drawGroups();
    return [strip, groupsEl];
  }
  function searchField(): HTMLInputElement {
    const search = node("input", "rx-field rx-search");
    search.type = "search";
    search.placeholder = "Search reports";
    search.setAttribute("inputmode", "search");
    search.setAttribute("aria-label", "Search reports");
    search.value = query;
    search.dataset["testid"] = "rx-search";
    return search;
  }
  function homeView(): HTMLElement {
    const home = node("div", "rx-home");
    home.dataset["testid"] = "rx-home";
    const head = node("div", "rx-head");
    const title = node("h1", undefined, "Reports");
    hoverHelp(title, "Your dashboard shows what needs attention and the numbers you chose. Every report is under All reports, with the star on any report to keep it near.", "", "star Sales summary to open it in one tap.");
    head.append(title, node("span", "rx-grow"));
    const newCustom = host.newCustomReport;
    if (dash === undefined) {
      // No dashboard for this console: the library stands on the home itself, as it always did.
      const search = searchField();
      head.append(search);
      if (newCustom !== undefined) head.append(button("+ New custom report", "rx-btn rx-btn-primary", () => newCustom(), "rx-new"));
      home.append(head);
      if (marksNotice !== "") {
        const text = node("p", "rx-notice", marksNotice);
        text.setAttribute("role", "status");
        home.append(text);
      }
      home.append(...libraryParts(search));
      return home;
    }
    const dashboard = dash;
    head.append(button("Customize", "rx-btn", () => dashboard.customize(() => screen.querySelector<HTMLElement>('[data-testid="rd-customize"]')), "rd-customize"));
    const all = button("All reports", "rx-btn", openDrawer, "rx-all-reports");
    all.append(node("span", "rd-count", String(cards().length)));
    head.append(all);
    if (newCustom !== undefined) head.append(button("+ New custom report", "rx-btn rx-btn-primary", () => newCustom(), "rx-new"));
    home.append(head);
    if (marksNotice !== "") {
      const text = node("p", "rx-notice", marksNotice);
      text.setAttribute("role", "status");
      home.append(text);
    }
    home.append(dashboard.element);
    return home;
  }
  function openDrawer(): void {
    drawerOpen = true;
    draw();
    queueMicrotask(() => screen.querySelector<HTMLElement>('[data-testid="rx-search"]')?.focus());
  }
  function closeDrawer(): void {
    drawerOpen = false;
    draw();
    queueMicrotask(() => screen.querySelector<HTMLElement>('[data-testid="rx-all-reports"]')?.focus());
  }
  /** REPORTDASH1: All reports, a right-hand drawer under the console header. Closing it returns to the dashboard as it was. */
  function drawerView(): HTMLElement {
    const wrap = node("div", "rd-drawer-wrap");
    const scrim = node("div", "rd-scrim");
    scrim.dataset["testid"] = "rx-drawer-scrim";
    scrim.addEventListener("click", closeDrawer);
    const drawer = node("aside", "rd-drawer");
    drawer.dataset["testid"] = "rx-drawer";
    drawer.setAttribute("role", "dialog");
    drawer.setAttribute("aria-label", "All reports");
    const head = node("div", "rd-drawer-head");
    const top = node("div", "rd-drawer-top");
    top.append(node("h2", undefined, "All reports"), node("span", "rx-grow"), button("Close", "rx-btn", closeDrawer, "rx-drawer-close"));
    const search = searchField();
    head.append(top, search);
    const body = node("div", "rd-drawer-body");
    body.append(...libraryParts(search));
    drawer.append(head, body);
    wrap.append(scrim, drawer);
    return wrap;
  }

  // ----- results
  function resultsView(current: CannedCatalogEntry, filters: CannedFilters, scope: CannedCatalogDto): HTMLElement {
    const results = node("div", "rx-results");
    results.dataset["testid"] = "rx-results";
    const crumb = node("div", "rx-crumb");
    const back = button("\u2039 Reports", "rx-back", goHome, "rx-back");
    const title = node("h1", undefined, displayTitle(current.title));
    title.dataset["testid"] = "rx-title";
    crumb.append(back, title, starButton(current.title, isFavorite(marks, current.id), () => void setFavorite(current.id, !isFavorite(marks, current.id)), "rx-star-current"));
    results.append(crumb);
    results.append(controlRow(current, filters, scope));
    if (notice !== "") {
      const text = node("p", notice !== "" && notice === okNotice ? "rx-notice ok" : "rx-notice", notice);
      text.setAttribute("role", "alert");
      text.dataset["testid"] = "rx-notice";
      if (noticeLink !== "") {
        const link = node("a", "rx-notice-link", "Open the sheet");
        link.href = noticeLink;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.dataset["testid"] = "rx-notice-link";
        text.append(" ", link);
      }
      results.append(text);
    }
    if (needsExtras(current)) results.append(extrasRow(current, filters));
    results.append(resultsBody(current));
    return results;
  }
  function controlRow(current: CannedCatalogEntry, filters: CannedFilters, scope: CannedCatalogDto): HTMLElement {
    const row = node("div", "rx-card rx-controls");
    row.dataset["testid"] = "rx-controls";
    const hasDates = current.id !== "current-inventory-value" && !hasNoDates(current);
    // UIFIX1 (SF-1): each filter is one line pill with its popover anchored under it; the popover is a 360px sheet.
    const sheet = (kind: "dates" | "locations", body: HTMLElement): HTMLElement => {
      const panel = node("div", `rx-pop ${kind}`);
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-label", kind === "dates" ? "Dates" : words.title);
      panel.dataset["testid"] = `rx-pop-${kind}`;
      panel.append(body);
      return panel;
    };
    if (hasDates) {
      const dates = filterPill("Dates", datesSummary(filters), "rx-dates", popover === "dates", () => {
        if (busy()) return;
        popover = popover === "dates" ? null : "dates";
        customDraft = { from: filters.from, to: filters.to };
        customError = "";
        draw();
      });
      dates.disabled = busy();
      row.append(pillWrap(dates, ...(popover === "dates" ? [sheet("dates", datesPanel(current, filters, scope))] : [])));
    }
    if (scope.scope.kind === "network") {
      const where = filterPill(words.title, scopeLabel(filters, scope, words), "rx-locations", popover === "locations", () => {
        if (busy()) return;
        popover = popover === "locations" ? null : "locations";
        draw();
      });
      where.disabled = busy();
      row.append(pillWrap(where, ...(popover === "locations" ? [sheet("locations", locationsPanel(current, filters, scope))] : [])));
    }
    row.append(node("span", "rx-grow"));
    const ready = result !== undefined && ranBody !== undefined;
    const items: ToolbarItem[] = [
      {
        testid: "rx-export",
        label: "Export CSV",
        disabled: busy() || !current.canExport || !ready,
        reason: current.canExport ? "" : "Not available for this report.",
        run: () => void exportCsv(),
      },
      ...exportButtons.items(),
      {
        testid: "rx-schedule",
        label: "Schedule email",
        disabled: busy() || !current.canSchedule || !ready,
        reason: current.canSchedule ? "" : "Scheduling is not available for this report.",
        run: () => void schedule(),
      },
    ];
    row.append(
      exportMenu({
        testid: "rx",
        open: popover === "export",
        toggle: () => {
          popover = popover === "export" ? null : "export";
          draw();
        },
        close: closePopover,
        items,
        disabled: busy() || !ready,
      }),
    );
    if (popover !== null) {
      const scrim = node("div", "rx-scrim");
      scrim.dataset["testid"] = "rx-scrim";
      scrim.addEventListener("click", closePopover);
      row.prepend(scrim);
    }
    return row;
  }
  function datesPanel(current: CannedCatalogEntry, filters: CannedFilters, scope: CannedCatalogDto): HTMLElement {
    const panel = node("div", "rx-pop-body");
    const head = node("div", "rx-pop-head");
    head.append(hoverHelp(node("span", "rx-pop-title", "Dates"), "Pick a ready made range or set your own. Turn on compare to see the period just before it.", "", "Last 30 days against the 30 days before."));
    panel.append(head);
    const presets = node("div", "rx-presets");
    for (const choice of presetChoices(current)) {
      const chip = button(choice.label, `rx-chip-btn${filters.preset === choice.preset ? " on" : ""}`, () => {
        popover = null;
        change({ ...filters, preset: choice.preset, from: "", to: "" });
      }, `rx-preset-${choice.preset}`);
      chip.setAttribute("aria-pressed", String(filters.preset === choice.preset));
      presets.append(chip);
    }
    panel.append(presets);
    // CLOSEFIX1: the pay period choices for Tips by employee, from the location's own payroll. A running period ends today.
    const periodChoices: PayPeriodChoice[] = current.id === "tips-by-employee-day" ? payPeriodChoices(payPeriodList, scope.scope.businessDate) : [];
    if (periodChoices.length > 0) {
      panel.append(node("div", "rx-label", "Pay period"));
      const periods = node("div", "rx-presets");
      for (const choice of periodChoices) {
        const on = filters.preset === "custom" && filters.from === choice.from && filters.to === choice.to;
        const chip = button(choice.label, `rx-chip-btn${on ? " on" : ""}`, () => {
          popover = null;
          change({ ...filters, preset: "custom", from: choice.from, to: choice.to });
        }, "rx-pay-period");
        chip.setAttribute("aria-pressed", String(on));
        periods.append(chip);
      }
      panel.append(periods);
    }
    const custom = node("div", "rx-custom");
    custom.append(node("div", "rx-label", "Custom range"));
    const inputs = node("div", "rx-custom-inputs");
    const date = (key: "from" | "to", label: string) => {
      const field = node("label", "rx-date");
      field.append(node("span", undefined, label));
      const input = node("input", "rx-field");
      input.type = "date";
      input.value = customDraft[key];
      input.max = scope.scope.businessDate;
      input.dataset["testid"] = `rx-custom-${key}`;
      input.addEventListener("change", () => {
        customDraft = { ...customDraft, [key]: input.value };
        customError = "";
      });
      field.append(input);
      return field;
    };
    inputs.append(date("from", "From"), date("to", "To"));
    const applyRange = button("Apply range", "rx-btn", () => {
      const next = { ...filters, preset: "custom", from: customDraft.from, to: customDraft.to };
      try {
        cannedRunBody(current, next, scope);
      } catch (cause) {
        customError = cause instanceof Error ? cause.message : "Choose a valid date range.";
        draw();
        return;
      }
      popover = null;
      change(next);
    }, "rx-custom-apply");
    inputs.append(applyRange);
    custom.append(inputs);
    if (customError !== "") {
      const text = node("p", "rx-notice", customError);
      text.setAttribute("role", "alert");
      custom.append(text);
    }
    panel.append(custom);
    if (compareKind(current) !== "") {
      const on = filters.compare !== "";
      const toggle = node("button", `rx-switch${on ? " on" : ""}`);
      toggle.type = "button";
      toggle.setAttribute("role", "switch");
      toggle.setAttribute("aria-checked", String(on));
      toggle.dataset["testid"] = "rx-compare";
      toggle.append(node("i"), node("span", undefined, "Compare to prior period"));
      toggle.addEventListener("click", () => {
        popover = null;
        change(withCompare(current, filters, !on));
      });
      panel.append(toggle);
    }
    return panel;
  }
  function locationsPanel(current: CannedCatalogEntry, filters: CannedFilters, scope: CannedCatalogDto): HTMLElement {
    const sites: PickerLocation[] = scope.scope.locations.map((site) => ({ id: site.id, name: site.name, franchisee: site.franchisee, suspended: site.suspended }));
    const modes: PickerMode[] = current.maxSelectedLocations === 1 ? ["one"] : ["one", "selected", "all"];
    return mountLocationPicker({
      locations: sites,
      value: pickerValue(filters.allLocations, filters.locationIds),
      modes,
      words,
      cancel: closePopover,
      apply: (picked) => {
        popover = null;
        change({ ...filters, ...toFilterScope(picked), countSessionId: "", baselineCountSessionId: "", comparisonCountSessionId: "" });
      },
    });
  }
  function needsExtras(current: CannedCatalogEntry): boolean {
    return current.id === "recorded-transfer-quantities" || current.requiresCountSession === true || current.requiresTwoCountSessions === true;
  }
  /** The few reports that cannot run on dates and locations alone: transfer direction and closed count sessions. */
  function extrasRow(current: CannedCatalogEntry, filters: CannedFilters): HTMLElement {
    const row = node("div", "rx-card rx-extras");
    row.dataset["testid"] = "rx-extras";
    if (current.id === "recorded-transfer-quantities") {
      const seg = node("div", "rx-seg");
      for (const direction of current.directions ?? []) {
        const choice = button(reportWords(direction), filters.direction === direction ? "on" : "", () => change({ ...filters, direction }), `rx-direction-${direction}`);
        choice.setAttribute("aria-pressed", String(filters.direction === direction));
        choice.disabled = busy();
        seg.append(choice);
      }
      row.append(seg);
    }
    if (current.requiresCountSession === true || current.requiresTwoCountSessions === true) {
      const load = button("Choose closed counts", "rx-btn", () => void loadSessions(), "rx-load-sessions");
      load.disabled = busy();
      row.append(load);
      const keys: Array<["countSessionId" | "baselineCountSessionId" | "comparisonCountSessionId", string]> =
        current.requiresTwoCountSessions === true
          ? [["baselineCountSessionId", "Baseline"], ["comparisonCountSessionId", "Comparison"]]
          : [["countSessionId", "Closed count"]];
      if (sessions.length > 0) {
        for (const [key, label] of keys) {
          const field = node("label", "rx-date");
          field.append(node("span", undefined, label));
          const select = node("select", "rx-field");
          select.append(new Option("Choose a count", ""));
          for (const session of sessions) select.append(new Option(session.label, session.id));
          select.value = filters[key];
          select.dataset["testid"] = `rx-session-${key}`;
          select.addEventListener("change", () => {
            applied = { ...filters, [key]: select.value };
            if (keys.every(([k]) => (applied as CannedFilters)[k] !== "")) void run();
          });
          field.append(select);
          row.append(field);
        }
      }
      if (sessionsNote !== "") row.append(node("span", "rx-notice", sessionsNote));
    }
    return row;
  }
  function resultsBody(current: CannedCatalogEntry): HTMLElement {
    if (status === "loading") return loadingState();
    if (status === "denied" || status === "busy" || status === "error") return failureState(status);
    if (status === "empty") return emptyState(current);
    if (result === undefined) {
      if (current.requiresCountSession === true || current.requiresTwoCountSessions === true) {
        const prompt = node("div", "rx-card rx-state rx-state-compact");
        prompt.dataset["testid"] = "rx-state-choose-counts";
        prompt.append(node("h2", undefined, current.requiresTwoCountSessions === true ? "Choose two closed counts" : "Choose a closed count"));
        return prompt;
      }
      return node("div");
    }
    return filled(current, result);
  }
  function loadingState(): HTMLElement {
    const wrap = node("div", "rx-body");
    wrap.dataset["testid"] = "rx-state-loading";
    wrap.setAttribute("role", "status");
    wrap.setAttribute("aria-busy", "true");
    wrap.setAttribute("aria-label", "Loading report");
    const top = node("div", "rx-vis");
    top.append(node("div", "rx-card rx-skel rx-skel-chart"), node("div", "rx-card rx-skel rx-skel-totals"));
    const table = node("div", "rx-card rx-skel-table");
    for (let index = 0; index < 5; index += 1) table.append(node("div", "rx-skel rx-skel-row"));
    wrap.append(top, table);
    return wrap;
  }
  function failureState(kind: RunFailure): HTMLElement {
    const state = node("div", "rx-card rx-state rx-state-compact");
    state.dataset["testid"] = `rx-state-${kind}`;
    state.setAttribute("role", kind === "busy" ? "status" : "alert");
    if (kind === "denied") {
      state.append(node("h2", undefined, "No access to this report"), node("p", undefined, "Ask an admin."));
    } else if (kind === "busy") {
      const heading = node("h2", undefined, "Another report is running here");
      hoverHelp(heading, "Only one report runs at a time at each location. Yours starts when the current one finishes.");
      state.append(heading, node("p", undefined, "Try again in a moment."), button("Try again", "rx-btn rx-btn-primary", () => void run(), "rx-retry"));
    } else {
      state.append(node("h2", undefined, "This report did not load"), node("p", undefined, failure), button("Try again", "rx-btn rx-btn-primary", () => void run(), "rx-retry"));
    }
    return state;
  }
  function emptyState(current: CannedCatalogEntry): HTMLElement {
    const state = node("div", "rx-card rx-state rx-state-compact");
    state.dataset["testid"] = "rx-state-empty";
    state.append(node("h2", undefined, hasNoDates(current) ? "Nothing recorded yet" : "Nothing recorded in these dates"));
    const actions = node("div", "rx-actions");
    if (applied !== undefined && presetChoices(current).some((choice) => choice.preset === "last_30_days") && applied.preset !== "last_30_days")
      actions.append(button("Show last 30 days", "rx-btn rx-btn-primary", () => change({ ...(applied as CannedFilters), preset: "last_30_days", from: "", to: "" }), "rx-longer"));
    if (current.id !== "current-inventory-value" && !hasNoDates(current))
      actions.append(button("Change dates", "rx-btn", () => {
        popover = "dates";
        customDraft = { from: applied?.from ?? "", to: applied?.to ?? "" };
        draw();
      }, "rx-change-dates"));
    state.append(actions);
    return state;
  }
  function filled(current: CannedCatalogEntry, data: CannedResultDto): HTMLElement {
    const wrap = node("div", "rx-body");
    const table = resultTable(data);
    const shown = displayTable(table, {
      singleLocation: data.scope.locationCount === 1,
      ...(catalog?.scope.timezone === undefined ? {} : { timeZone: catalog.scope.timezone }),
      currentYear: now().getFullYear(),
    });
    // UIFIX1 (SF-2): a summary holding one figure is the Total row, not a card of its own.
    const figures = totalFigures(data);
    const drawn = chartModel(data, current.title);
    // HIGFIX4B (F-43): a chart with one day of data is a lone dot or one tall column on empty axes, which reads as broken, so it is not drawn.
    const model = drawn !== undefined && (!drawn.ok || chartPointsWithData(drawn.model) >= 2) ? drawn : undefined;
    // HIGFIX4B (F-44): Daily loss prevention lists a day only when something happened on it; the Total row still carries every figure.
    // MGRDAYMAJ1 (manager-day-09): a day-by-day report opens newest first until the person sorts it themselves.
    const activeSort = sort ?? defaultSort(current.id, table);
    const orderedRows = sortRows(table, activeSort);
    // MGRDAYMAJ1 (manager-day-09): Daily sales lists a day only when something was sold on it, so yesterday is the first row, not the
    // fifth from the top under a run of $0.00 days.
    const idleRows = current.id === "loss-prevention-trends" || current.id === "daily-sales" ? orderedRows.filter((row) => isIdleRow(shown.columns, row)) : [];
    if (orderedRows.length > 0 && idleRows.length === orderedRows.length) {
      const quiet = node("div", "rx-card rx-state rx-state-compact");
      quiet.dataset["testid"] = "rx-state-quiet";
      quiet.append(node("h2", undefined, "No activity in this period"));
      wrap.append(quiet);
      return wrap;
    }
    // HIGFIX4B (F-42, F-45): with one location the table's Total row already says every figure the strip would repeat.
    if (figures.length > 1 && !(data.scope.locationCount === 1 && table.total !== null)) {
      const totals = node("div", "rx-card rx-totals");
      totals.dataset["testid"] = "rx-totals";
      for (const figure of figures) {
        const line = node("div", "rx-total");
        line.dataset["testid"] = "rx-total";
        line.append(node("span", undefined, figure.label));
        const value = node("div", "rx-total-value");
        value.append(node("b", "rx-num", figure.text));
        if (figure.change !== null) {
          const chip = node("span", `rx-delta ${figure.change.direction}`, figure.change.text);
          chip.setAttribute("aria-label", `${figure.change.direction === "flat" ? "no change" : figure.change.direction === "up" ? "up" : "down"} ${figure.change.text.slice(2)}`);
          value.append(chip);
        }
        line.append(value);
        totals.append(line);
      }
      wrap.append(totals);
    }
    // CLOSEFIX1 (manager-day-01): Tips by employee answers "who is owed what" first, one total per person; the daily rows follow.
    const owed = current.id === "tips-by-employee-day" ? owedPerPerson(data) : null;
    if (owed !== null && owed.length > 0) {
      wrap.append(node("h2", "rx-section", "Owed per person"));
      wrap.append(node("p", "rx-foot", "Each person's recorded card tips for these dates. Every total is the sum of that person's daily rows below."));
      const owedCard = node("div", "rx-card rx-table-card");
      owedCard.dataset["testid"] = "rx-owed";
      const owedTable = node("table", "rx-table");
      owedTable.append(node("caption", "rx-sr", "Tips owed per person"));
      const owedHead = node("tr");
      const nameHead = node("th", undefined, "Person");
      nameHead.scope = "col";
      const tipsHead = node("th", "num", "Tips");
      tipsHead.scope = "col";
      owedHead.append(nameHead, tipsHead);
      const owedThead = node("thead");
      owedThead.append(owedHead);
      owedTable.append(owedThead);
      const owedBody = node("tbody");
      const several = data.scope.locationCount > 1;
      for (const person of owed) {
        const tr = node("tr");
        tr.dataset["testid"] = "rx-owed-row";
        tr.append(node("td", "rx-lead", several && person.locationName !== "" ? `${person.name}, ${person.locationName}` : person.name), node("td", "num rx-num", formatCents(person.cents)));
        owedBody.append(tr);
      }
      owedTable.append(owedBody);
      const owedScroll = node("div", "rx-table-scroll");
      owedScroll.append(owedTable);
      owedCard.append(owedScroll);
      wrap.append(owedCard);
      wrap.append(node("h2", "rx-section", "Daily rows"));
      wrap.append(node("p", "rx-foot", "The days behind each total, one row per person per day."));
    }
    const tableCard = node("div", "rx-card rx-table-card");
    tableCard.dataset["testid"] = "rx-table";
    const scroll = node("div", "rx-table-scroll");
    scroll.tabIndex = 0;
    scroll.setAttribute("aria-label", `${current.title} table`);
    const element = node("table", "rx-table");
    element.append(node("caption", "rx-sr", current.title));
    const head = node("thead");
    const headRow = node("tr");
    for (const column of shown.columns) {
      const th = node("th", column.numeric ? "num" : "");
      th.scope = "col";
      th.setAttribute("aria-sort", activeSort?.key === column.key ? (activeSort.dir === "asc" ? "ascending" : "descending") : "none");
      const sortButton = button(column.label, `rx-th${activeSort?.key === column.key ? " on" : ""}`, () => {
        sort = nextSort(table, activeSort, column.key);
        draw();
        screen.querySelector<HTMLElement>(`[data-testid="rx-th-${column.key}"]`)?.focus();
      }, `rx-th-${column.key}`);
      if (activeSort?.key === column.key) sortButton.append(node("span", "rx-arrow", activeSort.dir === "asc" ? " \u25B2" : " \u25BC"));
      th.append(sortButton);
      headRow.append(th);
    }
    head.append(headRow);
    element.append(head);
    const body = node("tbody");
    for (const row of orderedRows) {
      if (idleRows.includes(row)) continue;
      const tr = node("tr");
      tr.dataset["testid"] = "rx-row";
      for (const column of shown.columns) {
        const td = node("td", column.numeric ? "num rx-num" : column.shape === "lead" ? "rx-lead" : "rx-text");
        if (column.change && typeof row.values[column.key] === "number") {
          td.append(changeCell(row.values[column.key] as number));
        } else if (column.shape === "lead") {
          // Tap the first column to read the whole row: anything the table cuts off is here in full (never a hover title).
          const opener = button("", "rx-row-open", () => {
            detail = { row, view: shown };
            draw();
            screen.querySelector<HTMLElement>('[data-testid="rx-row-sheet-close"]')?.focus();
          }, "rx-row-open");
          opener.append(node("span", "rx-lead-name", shown.cell(row, column)));
          const note = shown.note(row);
          if (note !== "") opener.append(node("small", "rx-lead-note", note));
          td.append(opener);
        } else td.textContent = shown.cell(row, column);
        tr.append(td);
      }
      body.append(tr);
    }
    element.append(body);
    const oneFigure = figures.length === 1 ? figures[0] : undefined;
    if (table.total !== null || oneFigure !== undefined) {
      const foot = node("tfoot");
      const tr = node("tr", "rx-total-row");
      tr.dataset["testid"] = "rx-total-row";
      let placed = false;
      for (const column of shown.columns) {
        const td = node("td", column.numeric ? "num rx-num" : "");
        if (table.total !== null && column.change && typeof table.total.values[column.key] === "number") td.append(changeCell(table.total.values[column.key] as number));
        else td.textContent = table.total === null ? (column.shape === "lead" ? "Total" : "") : shown.totalCell(column);
        if (table.total === null && oneFigure !== undefined && !placed && column.numeric) {
          td.textContent = oneFigure.text;
          placed = true;
        }
        tr.append(td);
      }
      // A total with no figure column of its own (a list of names) shows its one figure at the end of the row.
      if (table.total === null && oneFigure !== undefined && !placed) {
        const last = tr.lastElementChild;
        if (last !== null && shown.columns.length > 1) last.textContent = oneFigure.text;
        else if (last !== null) last.textContent = `Total ${oneFigure.text}`;
        (last as HTMLElement | null)?.classList.add("num", "rx-num");
      }
      foot.append(tr);
      element.append(foot);
    }
    scroll.append(element);
    tableCard.append(scroll);
    const where = data.scope.locationCount;
    // UIFIX1 (SF-3): the scope count says how many rows came from how many places once, instead of repeating the scope on every report.
    const rowCount = table.rows.length;
    const scope = `${where} ${where === 1 ? words.singular : words.plural}`;
    const parts = [
      catalog?.scope.kind === "network" ? (rowCount === where ? scope : `${rowCount} ${rowCount === 1 ? "row" : "rows"} from ${scope}`) : "",
      data.display.truncated ? "Showing the first rows. Export CSV has every row" : "",
      idleRows.length > 0 ? `${idleRows.length} ${idleRows.length === 1 ? "day" : "days"} with no activity not shown` : "",
      `Updated ${whenText(data.generatedAt, now())}`,
    ].filter((part) => part !== "");
    const note = node("div", "rx-foot", parts.join(" \u00B7 "));
    note.dataset["testid"] = "rx-foot";
    tableCard.append(note);
    wrap.append(tableCard);
    if (model !== undefined) wrap.append(chartSection(model));
    return wrap;
  }
  /** UIFIX1 (SF-2): the chart sits under the table, can be hidden, and fills the card at 1:1 so its labels are real 12px text. */
  function chartSection(model: ChartResult): HTMLElement {
    const isOpen = chartOpen ?? (typeof window.matchMedia === "function" ? window.matchMedia("(min-width: 1100px)").matches : true);
    const section = node("div", "rx-chart-section");
    // HIGFIX4B (F-41): the toggle is a small text control on the chart card, not a full-width button between two cards.
    const toggle = button(isOpen ? "Hide chart" : "Show chart", "rx-chart-toggle", () => {
      chartOpen = !isOpen;
      draw();
    }, "rx-chart-toggle");
    toggle.setAttribute("aria-expanded", String(isOpen));
    if (isOpen) {
      const chart = node("div", "rx-card rx-chart");
      chart.dataset["testid"] = "rx-chart";
      const actions = node("div", "rx-chart-actions");
      actions.append(toggle);
      chart.append(actions);
      section.append(chart);
      const room = Math.max(560, Math.min(1400, Math.floor((screen.clientWidth || 1100) - 2)));
      chart.append(renderChartCard(model, room));
    } else {
      section.append(toggle);
    }
    return section;
  }
  function rowSheet(): HTMLElement | undefined {
    if (detail === undefined) return undefined;
    const current = detail;
    const scrim = node("div", "rx-sheet-scrim");
    scrim.dataset["testid"] = "rx-row-sheet-scrim";
    scrim.addEventListener("click", (event) => {
      if (event.target !== scrim) return;
      detail = undefined;
      draw();
    });
    const sheet = node("div", "rx-sheet");
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-label", "Row details");
    sheet.dataset["testid"] = "rx-row-sheet";
    const list = node("dl", "rx-sheet-list");
    for (const line of rowDetails(current.view, current.row)) list.append(node("dt", undefined, line.label), node("dd", undefined, line.text));
    sheet.append(node("h2", undefined, "Details"), list, button("Close", "rx-btn rx-btn-primary", () => {
      detail = undefined;
      draw();
    }, "rx-row-sheet-close"));
    scrim.append(sheet);
    return scrim;
  }
  function changeCell(basisPoints: number): HTMLElement {
    const direction = basisPoints > 0 ? "up" : basisPoints < 0 ? "down" : "flat";
    const magnitude = (Math.abs(basisPoints) / 100).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const chip = node("span", `rx-delta ${direction}`, `${direction === "up" ? "▲" : direction === "down" ? "▼" : "▬"} ${magnitude}%`);
    chip.setAttribute("aria-label", `${direction === "flat" ? "no change" : direction} ${magnitude}%`);
    return chip;
  }

  void loadCatalog();
  void loadMarks();
  exportButtons.load();
  return {
    destroy() {
      alive = false;
      dash?.destroy();
      exportButtons.destroy();
      generation++;
      stopEscape();
      hideHoverHelp();
      root.remove();
    },
    async refresh() {
      void dash?.load();
      await loadCatalog();
      await loadMarks();
    },
  };
}
